import React, { useState } from 'react';
import { ArrowLeft, Clock, Check, AlertTriangle, Calculator, QrCode, Package, ChevronDown, ChevronRight, Play, Snowflake } from 'lucide-react';

/**
 * TaskDetailScreen — Detalhe de uma OF
 * Layout: header navy (3 linhas: nº OF, modelo grande, marca),
 * Operações organizadas por Lote (expandir lote → esconde outros + botão voltar)
 */
export default function TaskDetailScreen({
  of,
  operacoes = [],
  operacoesCongeladas = [],
  user,
  onBack,
  onSelectOperacao,
  onScanLote,
  modoTestes = false,
  preSelectedLoteId = null,
}) {
  const [expandedLoteId, setExpandedLoteId] = useState(preSelectedLoteId);

  if (!of) return null;

  const lotes = of.lotes || [];

  // Build operations per lote
  const opsAtribuidas = lotes.flatMap(l =>
    (l.operacoes_operador || []).map(op => ({ ...op, lote: l }))
  );

  const temCongelada = (opId) =>
    operacoesCongeladas.some(c => c.operacao_id === opId && c.of_id === of.id);

  const isInProgress = (op) => {
    const produzido = op.quantidade_produzida_todos ?? op.quantidade_produzida ?? 0;
    return produzido > 0 && produzido < (op.quantidade_total || of.quantidade_total || 1);
  };

  const isDone = (op) => {
    // Use backend status if available (respects status_gravado='concluida')
    if (op.status === 'completa') return true;
    // Fallback to calculation for backwards compatibility
    const max = op.quantidade_total || of.quantidade_total || 1;
    const produzido = op.quantidade_produzida_todos ?? op.quantidade_produzida ?? 0;
    return produzido >= max;
  };

  const hasAnomalias = (op) => (op.defeitos || 0) + (op.arranjos || 0) > 0;

  // Group operations by lote
  const loteGroups = lotes
    .filter(l => l.status === 'aprovado')
    .map(lote => {
      const loteOps = opsAtribuidas.filter(op => (op.lote_id || op.lote?.id) === lote.id);
      const allDone = loteOps.length > 0 && loteOps.every(op => isDone(op));
      const doneCount = loteOps.filter(op => isDone(op)).length;
      const loteCode = lote.nome_lote || lote.codigo_lote || `L${lote.numero_lote}`;
      const loteQty = lote.quantidade_ajustada || lote.quantidade || 0;
      const categoriaNome = lote.categoria_nome || lote.categoria || '';
      const categoriaCor = lote.categoria_cor || '';
      return { lote, loteCode, loteQty, categoriaNome, categoriaCor, ops: loteOps, allDone, doneCount };
    });

  // Split into active and completed
  const activeLotes = loteGroups.filter(lg => !lg.allDone);
  const completedLotes = loteGroups.filter(lg => lg.allDone);

  const expandLote = (loteId) => {
    setExpandedLoteId(loteId);
  };

  const collapseLote = () => {
    setExpandedLoteId(null);
  };

  // If a lote is expanded, only show that one
  const expandedGroup = expandedLoteId ? loteGroups.find(lg => lg.lote.id === expandedLoteId) : null;

  return (
    <div className="flex flex-col h-screen pb-20" style={{ background: '#F8FAFC' }}>
      {/* Header navy — 3 linhas: nº OF, modelo, marca */}
      <div style={{ background: '#1E293B' }}>
        <div className="px-4 pt-4 pb-3">
          {/* Top row: Back + OF number + badges + QR */}
          <div className="flex items-center gap-3 mb-1">
            <button
              onClick={onBack}
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.15)' }}
              data-testid="btn-back-task-detail"
            >
              <ArrowLeft className="h-5 w-5 text-white" />
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="font-black text-white text-2xl leading-none">{of.numero_of}</span>
              {of.urgente && (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold text-white" style={{ background: '#EF4444' }}>URGENTE</span>
              )}
            </div>
            {onScanLote && (
              <button
                onClick={onScanLote}
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.15)' }}
                data-testid="btn-digitalizar-lote"
              >
                <QrCode className="h-5 w-5 text-white" />
              </button>
            )}
          </div>
          {/* Modelo — grande */}
          <p className="text-xl font-extrabold ml-12" style={{ color: 'rgba(255,255,255,0.95)' }} data-testid="task-detail-modelo">
            {of.artigo}
          </p>
          {/* Marca — pequena */}
          <p className="text-sm font-medium ml-12 mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }} data-testid="task-detail-marca">
            {of.cliente}
          </p>
        </div>
      </div>

      {/* Lista de lotes com operações */}
      <div className="flex-1 px-4 py-3 overflow-y-auto">

        {/* If a lote is expanded, show only that lote with a back button */}
        {expandedGroup ? (
          <div>
            {/* Back to all lotes button */}
            <button
              onClick={collapseLote}
              className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl transition-colors active:bg-stone-100"
              style={{ color: '#64748B' }}
              data-testid="btn-voltar-lotes"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="font-bold text-sm">Voltar aos lotes</span>
            </button>

            {/* Lote header */}
            <div className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div className="flex items-center gap-3 px-4 py-3.5" style={{ background: expandedGroup.categoriaCor || '#0B3B30' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-lg text-white">{expandedGroup.loteCode}</span>
                      {expandedGroup.categoriaNome && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: 'rgba(255,255,255,0.2)', color: '#FFFFFF' }}>
                          {expandedGroup.categoriaNome}
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-bold text-white">{expandedGroup.loteQty} un.</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {(expandedGroup.lote?.categoria_codigo === 'ARR' || expandedGroup.lote?.origem === 'arranjo') ? (
                      <span className="text-xs font-medium text-white/80">Trabalho de arranjo · {expandedGroup.loteQty} peça(s)</span>
                    ) : (
                      <>
                        <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.2)' }}>
                          <div className="h-full rounded-full" style={{ width: `${expandedGroup.ops.length > 0 ? Math.round((expandedGroup.doneCount / expandedGroup.ops.length) * 100) : 0}%`, background: '#10B981' }} />
                        </div>
                        <span className="text-xs font-bold text-white flex-shrink-0">
                          {expandedGroup.doneCount}/{expandedGroup.ops.length}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Operations */}
              <div className="px-3 py-3 space-y-2">
                {(() => {
                  const isArrLote = expandedGroup.lote?.categoria_codigo === 'ARR' || expandedGroup.lote?.origem === 'arranjo';
                  if (!isArrLote) return null;
                  const desc = expandedGroup.lote?.descricao || expandedGroup.lote?.observacoes;
                  if (!desc) return null;
                  return (
                    <div className="rounded-xl px-4 py-3" style={{ background: '#FEF2F2', border: '1.5px solid #FCA5A5' }} data-testid="arr-descricao-banner">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#FEE2E2', color: '#B91C1C' }}>ARR</span>
                        <span className="font-black text-sm" style={{ color: '#0B3B30' }}>Tarefa de arranjo</span>
                      </div>
                      <p className="text-sm font-bold" style={{ color: '#1E293B' }}>{desc}</p>
                      <p className="text-xs mt-1" style={{ color: '#64748B' }}>{expandedGroup.loteQty} peça(s) · OF {expandedGroup.lote?.numero_of || ''}</p>
                    </div>
                  );
                })()}

                {(() => {
                  const isArrLote = expandedGroup.lote?.categoria_codigo === 'ARR' || expandedGroup.lote?.origem === 'arranjo';
                  const permitidas = expandedGroup.lote?.operacoes_permitidas || [];
                  const opMaeId = expandedGroup.lote?.operacao_mae_id || null;
                  const opsParaListar = isArrLote && permitidas.length > 0
                    ? expandedGroup.ops.filter(op => permitidas.includes(op.operacao_id))
                    : expandedGroup.ops;
                  // Ordenar: operação mãe primeiro
                  if (isArrLote && opMaeId) {
                    opsParaListar.sort((a, b) => {
                      if (a.operacao_id === opMaeId) return -1;
                      if (b.operacao_id === opMaeId) return 1;
                      return 0;
                    });
                  }
                  // Estado da mãe: concluída ou não (para bloquear as restantes)
                  const opMae = isArrLote && opMaeId ? expandedGroup.ops.find(o => o.operacao_id === opMaeId) : null;
                  const maeConcluida = opMae ? isDone(opMae) : false;
                  return opsParaListar.map((op, idx) => {
                  const loteQty = expandedGroup.loteQty;
                  const produzida = op.quantidade_produzida_todos ?? op.quantidade_produzida ?? 0;
                  const done = isDone(op);
                  const inProgress = isInProgress(op);
                  const congelada = temCongelada(op.operacao_id);
                  const opInfo = operacoes.find(o => o.id === op.operacao_id);
                  const isContagem = op.is_contagem || opInfo?.is_contagem || false;
                  const hasZeroQty = loteQty === 0 && produzida === 0;
                  const isParcial = op.status === 'parcial' || op.status === 'parcial_outros';
                  const disponivel = op.disponivel != null ? op.disponivel : Math.max(0, loteQty - produzida);
                  const isMae = isArrLote && opMaeId && op.operacao_id === opMaeId;
                  const lockedByMae = isArrLote && opMaeId && !isMae && !maeConcluida;

                  return (
                    <div
                      key={op.operacao_id + '-' + idx}
                      className="rounded-xl overflow-hidden"
                      style={{
                        background: lockedByMae ? '#F8FAFC' : '#FFFFFF',
                        opacity: lockedByMae ? 0.6 : 1,
                        border: isMae ? '2px solid #DC2626' : done ? '1.5px solid #10B981' : congelada ? '1.5px solid #3B82F6' : isParcial ? '1.5px solid #F97316' : inProgress ? '1.5px solid #F59E0B' : lockedByMae ? '1.5px dashed #94A3B8' : '1.5px solid #E2E8F0',
                      }}
                    >
                      <div className="px-3 py-4 flex items-center gap-3">
                        {/* Left: Operation info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            {done ? (
                              <Check className="h-5 w-5 flex-shrink-0" style={{ color: '#10B981' }} />
                            ) : congelada ? (
                              <Snowflake className="h-5 w-5 flex-shrink-0" style={{ color: '#3B82F6' }} />
                            ) : inProgress ? (
                              <Play className="h-5 w-5 flex-shrink-0" style={{ color: '#F59E0B' }} />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 flex-shrink-0" style={{ borderColor: '#CBD5E1' }} />
                            )}
                            <span className="font-black text-base" style={{ color: '#0B3B30' }}>
                              {op.operacao_codigo || op.codigo}
                            </span>
                            {isMae && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-extrabold" style={{ background: '#FEE2E2', color: '#B91C1C' }}>
                                MÃE
                              </span>
                            )}
                            {lockedByMae && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#E2E8F0', color: '#475569' }}>
                                BLOQUEADA · concluir mãe
                              </span>
                            )}
                            {isContagem && (
                              <span className="text-[10px] px-1 py-0.5 rounded font-bold" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>
                                CONTAGEM
                              </span>
                            )}
                            {isParcial && (
                              <span className="text-[10px] px-1 py-0.5 rounded font-bold" style={{ background: '#FFF7ED', color: '#EA580C' }}>
                                PARCIAL
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium truncate pl-6" style={{ color: '#475569' }}>
                            {op.operacao_nome || opInfo?.nome}
                          </p>
                          <div className="flex items-center gap-2 pl-6 mt-1">
                            <span className="text-xs font-bold" style={{ color: done ? '#10B981' : '#64748B' }}>
                              {produzida}/{loteQty}
                            </span>
                            {!done && disponivel > 0 && (
                              <span className="text-xs font-semibold" style={{ color: '#2563EB' }}>
                                Disp: {disponivel}
                              </span>
                            )}
                          </div>
                          {hasAnomalias(op) && (
                            <p className="text-xs font-bold mt-1 pl-6 flex items-center gap-1" style={{ color: '#B45309' }}>
                              <AlertTriangle className="h-3 w-3" /> {(op.defeitos || 0) + (op.arranjos || 0)} anomalia(s)
                            </p>
                          )}
                        </div>

                        {/* Right: Action button (inline) */}
                        <div className="flex-shrink-0">
                          {done ? (
                            <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg" style={{ background: '#DCFCE7' }}>
                              <Check className="h-3.5 w-3.5" style={{ color: '#166534' }} />
                              <span className="text-xs font-bold" style={{ color: '#166534' }}>OK</span>
                            </div>
                          ) : hasZeroQty ? (
                            <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg" style={{ background: '#FEE2E2' }}>
                              <AlertTriangle className="h-3.5 w-3.5" style={{ color: '#991B1B' }} />
                              <span className="text-xs font-bold" style={{ color: '#991B1B' }}>0 un.</span>
                            </div>
                          ) : lockedByMae ? (
                            <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg" style={{ background: '#E2E8F0' }} data-testid={`btn-locked-mae-${op.operacao_codigo || op.codigo}`}>
                              <span className="text-xs font-bold" style={{ color: '#475569' }}>Bloqueada</span>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); onSelectOperacao && onSelectOperacao(op, expandedGroup.lote); }}
                              className="flex items-center gap-1.5 px-4 py-2 rounded-xl font-extrabold text-xs transition-all active:scale-95"
                              style={{
                                background: isParcial ? '#F97316' : inProgress || congelada ? '#0B3B30' : '#FFFFFF',
                                color: isParcial ? '#FFFFFF' : inProgress || congelada ? '#FFFFFF' : '#0B3B30',
                                border: isParcial ? '2px solid #F97316' : '2px solid #0B3B30',
                              }}
                              data-testid={`btn-iniciar-op-${op.operacao_codigo || op.codigo}-${expandedGroup.loteCode}`}
                            >
                              {isParcial ? 'Continuar' : inProgress || congelada ? 'Continuar' : 'Iniciar'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
                })()}
              </div>
            </div>
          </div>
        ) : (
          /* Normal view: show all lotes as clickable cards */
          <>
            <p className="font-extrabold text-sm uppercase tracking-wide mb-3" style={{ color: '#64748B' }}>
              LOTES ({activeLotes.length})
            </p>

            {activeLotes.length === 0 && completedLotes.length === 0 ? (
              <div className="text-center py-10">
                <p style={{ color: '#94A3B8' }}>Nenhuma operação atribuída</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeLotes.map((lg) => {
                  const progressPercent = lg.ops.length > 0 ? Math.round((lg.doneCount / lg.ops.length) * 100) : 0;

                  return (
                    <div
                      key={lg.lote.id}
                      className="rounded-2xl overflow-hidden cursor-pointer active:bg-stone-50 transition-colors"
                      style={{ background: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                      onClick={() => expandLote(lg.lote.id)}
                      data-testid={`lote-row-${lg.loteCode}`}
                    >
                      <div className="flex items-center gap-3 px-4 py-3.5">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: lg.categoriaCor || '#0B3B30' }}>
                          <Package className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-extrabold text-lg" style={{ color: '#1E293B' }}>{lg.loteCode}</span>
                              {lg.categoriaNome && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: lg.categoriaCor ? `${lg.categoriaCor}20` : '#E0F2FE', color: lg.categoriaCor || '#0284C7' }}>
                                  {lg.categoriaNome}
                                </span>
                              )}
                            </div>
                            <span className="text-sm font-bold" style={{ color: '#64748B' }}>{lg.loteQty} un.</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
                              <div className="h-full rounded-full" style={{ width: `${progressPercent}%`, background: '#F59E0B' }} />
                            </div>
                            <span className="text-sm font-bold flex-shrink-0" style={{ color: '#94A3B8' }}>
                              {lg.doneCount}/{lg.ops.length}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 flex-shrink-0" style={{ color: '#94A3B8' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Lotes completamente concluídos */}
            {completedLotes.length > 0 && (
              <div className="mt-4">
                <p className="font-extrabold text-xs uppercase tracking-wide mb-2" style={{ color: '#10B981' }}>
                  LOTES CONCLUIDOS ({completedLotes.length})
                </p>
                <div className="space-y-2">
                  {completedLotes.map(lg => (
                    <div
                      key={lg.lote.id}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl"
                      style={{ background: '#F0FDF4', border: '1px solid #D1FAE5' }}
                      data-testid={`lote-completo-${lg.loteCode}`}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#10B981' }}>
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <span className="font-bold text-sm" style={{ color: '#0B3B30' }}>
                          {lg.loteCode} — {lg.loteQty} un.
                        </span>
                        <p className="text-xs" style={{ color: '#64748B' }}>
                          {lg.ops.length} operação(ões) concluída(s)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
